import { LowerCasePipe } from '@angular/common';
import { Injectable, PipeTransform } from '@angular/core';
import { SortDirection } from '@shared/directives/sortable.directive';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ExportService } from './export.service';
import ticketAPIConfig from 'src/app/modules/apps/new-ticketing-dashboard/new-ticketing-dashboard-api.config.json';
import { ApiService } from './api.service';
import aiopsConfig from '../../apps/aiops-new/aiops-new-api.config.json';
import { ToastService } from '@shared/modules/toast/services/toast.service';
import { HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { DataStoreService } from './dataStore.service';
import KnowledgeAPIConfig from 'src/app/modules/apps/knowledge-base/kb-api.config.json';
import { RunTimeConfigService } from '@shared/services/run-time-config.service';
import { ExceptionHandlingService } from '@shared/services/exception-handling.service';
import { DateTimeService } from './date-time.service';
import moment from 'moment';

interface SearchResult {
  countries: any[];
  total: number;
  totalSortedData: any[];
}
interface State {
  page: number;
  pageSize: number;
  searchTerm: string;
  sortColumn: string;
  sortDirection: SortDirection;
}
function compare(v1: any, v2: any) {
  v1 = v1
    .toString()
    .toLowerCase()
    .trim();
  v2 = v2
    .toString()
    .toLowerCase()
    .trim();
  return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
}

function compareSla(v1: any, v2: any) {
  const currentDate: any = new Date();
  const firstDiff = v1 ? v1 - currentDate : v1;
  const secDiff = v2 ? v2 - currentDate : v2;
  return firstDiff < secDiff ? -1 : firstDiff > secDiff ? 1 : 0;
}

function sort(countries: any[], column: string, direction: string): any[] {
  if (direction === '') {
    return countries;
  } else {
    if (column !== 'sla') {
      return [...countries].sort((a, b) => {
        a[column] = a[column] === null ? '' : a[column];
        b[column] = b[column] === null ? '' : b[column];
        const res = compare(a[column], b[column]);
        return direction === 'asc' ? res : -res;
      });
    } else {
      return [...countries].sort((a, b) => {
        const aCol: any = a[column] ? new Date(a[column]) : a[column];
        const bCol: any = b[column] ? new Date(b[column]) : b[column];
        const currDate = new Date();
        const res = compareSla(aCol, bCol);
        return direction === 'asc' ? res : -res;
      });
    }
  }
}

@Injectable({ providedIn: 'root' })
export class NewTicketsService {
  public dateRangeForTktReprocess;
  public chartToggle;
  public errorMsg =
    'We are currently facing technical difficulties communicating your request, please try again later!';
  public globalSelectedTicketAssigment: any;
  public globalTicketList: any;
  assignmentGroups: any;
  public linkKOId: any;
  statusConstants: any;
  private _autoTaskSelected$ = true;
  private _isTicketListAccessError$: boolean;
  ticketDataType: any;
  dashboardData: any[];
  loggedInUser: any;
  private _search$ = new Subject<void>();
  private _state: State = {
    page: 1,
    pageSize: 20, // to be changed to the number of records as per pagination
    searchTerm: '',
    sortColumn: '',
    sortDirection: ''
  };
  private _isError$: boolean;
  ticketingSettings: any;
  private _total$ = new BehaviorSubject<number>(0);

  private _countries$ = new BehaviorSubject<any[]>([]);

  private _totalData$ = new BehaviorSubject<any[]>([]);
  private _loading$ = new BehaviorSubject<boolean>(true);

  public allDashboardData: any = [];
  loadfromDbFlag: any;
  constructor(
    private pipe: LowerCasePipe,
    private apiService: ApiService,
    private authService: AuthService,
    private dataStore: DataStoreService,
    private runTimeConfigService: RunTimeConfigService,
    private exportService: ExportService,
    private toastService: ToastService,
    private exceptionHandlingService: ExceptionHandlingService,
    private dateTimeService: DateTimeService
  ) {
    this.runTimeConfigService
      .getAppSpecificSettings('ticketing-new-settings.config.json')
      .subscribe(ticketingSettings => {
        this.ticketingSettings = ticketingSettings;
      });
  }

  get autoTaskSelected$() {
    return this._autoTaskSelected$;
  }

  set autoTaskSelected$(value: any) {
    this._autoTaskSelected$ = value;
  }

  get isTicketListAccessError$() {
    return this._isTicketListAccessError$;
  }

  set isTicketListAccessError$(value: boolean) {
    this._isTicketListAccessError$ = value;
  }

  get page() {
    return this._state.page;
  }

  set page(page: number) {
    this._set({ page });
  }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  get pageSize() {
    return this._state.pageSize;
  }

  set pageSize(pageSize: number) {
    this._set({ pageSize });
  }

  get isTicketError$() {
    return this._isError$;
  }

  set isTicketError$(value: boolean) {
    this._isError$ = value;
  }

  setStatusConstants(statusConstants: any) {
    this.statusConstants = statusConstants;
  }

  registerLike(uid: string, ticketId: string, toggle?: string) {
    return this.apiService.post(ticketAPIConfig.userFeedBack, {
      solutionnumber: uid,
      incidentnumber: ticketId,
      incident_type: toggle
    });
  }

  downloadAtttachmentSysID(attachType: any, fileName: string, attachID: string, sysId: any, ticketNumber: any) {
    const extension = fileName.replace(/^.*\./, '');
    const fileNameNoExt = fileName.replace(/\.[^/.]+$/, '');
    this.apiService
      .post(KnowledgeAPIConfig.downloadattachmentEI, {
        sys_id: sysId,
        number: ticketNumber,
        attachment_id: attachID,
        type: attachType
      })
      .subscribe(
        (res: any) => {
          if (res) {
            this.exportService.saveFileBase64(res.base64, fileNameNoExt, extension);
          } else {
            this.toastService.showToast(
              'lang.commonComponents.commonWords.commonToasterMessage.unexpectedFileData',
              'toastError',
              { clearToasts: true }
            );
          }
        },
        error => {
          // this.resetTicketData();
          this.toastService.showToast(
            'lang.commonComponents.commonWords.commonToasterMessage.unexpectedFileData',
            'toastError',
            { clearToasts: true }
          );
        }
      );
  }

  registerLikeEKS(kb_sys_id: string, kb_article: string, toggle: string) {
    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;
    const endPoint = toggle === 'ServiceRequests' ? ticketAPIConfig.updateServiceTask : ticketAPIConfig.updateTicket;

    // todo: Need to update this code once api configs are moved to root level
    return this.apiService.post(endPoint, {
      sys_id: kb_sys_id,
      kb_article,
      created_by: 'CommandTower'
    });
  }

  getAssignmentGroupForUser(user_sys_id: string) {
    return this.apiService.post(ticketAPIConfig.getGroupsBasedOnUser, {
      id: user_sys_id
    });
  }

  getTicketingList(selectedDropDown, payloadBody, selectedToggle) {
    let endPoint;
    if (selectedToggle === 'Incident') {
      this.allDashboardData = [];
      if (selectedDropDown === 'myTickets') {
        endPoint = ticketAPIConfig.getIncidentKOListBasedOnUser;
      }
      if (selectedDropDown === 'assignmentGroupTickets') {
        endPoint = ticketAPIConfig.getIncidentKOListBasedOnGroup;
      }
      if (selectedDropDown === 'myApprovals') {
        endPoint = ticketAPIConfig.getIncidentForAutomationApproval;
      }
    }
    if (selectedToggle === 'ServiceRequests') {
      this.allDashboardData = [];
      if (selectedDropDown === 'myTickets') {
        endPoint = ticketAPIConfig.serviceTaskListBasedOnUser;
      }
      if (selectedDropDown === 'assignmentGroupTickets') {
        endPoint = ticketAPIConfig.serviceTaskListBasedOnGroup;
      }
      if (selectedDropDown === 'myApprovals') {
        endPoint = ticketAPIConfig.getServiceTaskListForSolutionApproval;
      }
    }

    return this.apiService.post(endPoint, payloadBody).pipe(
      map((response: any) => {
        this.allDashboardData = response;
        return response;
      }),
      catchError(error => {
        this.isTicketListAccessError$ = this.exceptionHandlingService.isAccessUnauthorized(error);
        return throwError(error);
      })
    );
  }
  getDays(ms: number) {
    let isNeagativeVal: boolean;
    if (ms < 0) {
      isNeagativeVal = true;
      ms = Math.abs(ms);
    }
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const daysms = ms % (24 * 60 * 60 * 1000);
    const hours = Math.floor(daysms / (60 * 60 * 1000));
    const hoursms = ms % (60 * 60 * 1000);
    const minutes = Math.floor(hoursms / (60 * 1000));
    const minutesms = ms % (60 * 1000);
    const sec = Math.floor(minutesms / 1000);
    if (isNeagativeVal) {
      return '-' + days + ':' + hours + ':' + minutes + ':' + sec;
    } else {
      return days + ':' + hours + ':' + minutes + ':' + sec;
    }
  }

  getExpiryTime(slaDate: number): string {
    let expiryTime = '';
    if (slaDate) {
      // const result = slaDate.split(' ');
      // const date = result[0].trim().split('-');
      // const time = result[1].trim().split(':');
      // const endDate = new Date();
      // const purchaseDate = new Date(
      //   parseInt(date[0], 10),
      //   parseInt(date[1], 10) - 1,
      //   parseInt(date[2], 10),
      //   parseInt(time[0], 10),
      //   parseInt(time[1], 10),
      //   parseInt(time[2], 10)
      // );
      // const diffMs = Math.abs(endDate.getTime() - purchaseDate.getTime()); // milliseconds
      // const diffDays = Math.floor(diffMs / 86400000); // days
      // const diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
      // const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
      // expiryTime = diffDays ? diffDays + ' days ' + diffHrs + ' hours' : diffHrs + ' hours';
      // return expiryTime;
      // const milisec = +slaDate * 60 * 1000;
      const getTime = this.getDays(slaDate);
      const daysArr = getTime.split(':');
      if (daysArr[0] === '0') {
        expiryTime = daysArr[1] + ' hours ' + daysArr[2] + 'min';
      } else if (daysArr[0] === '-0') {
        expiryTime = '-' + daysArr[1] + ' hours ' + daysArr[2] + 'min';
      } else {
        expiryTime = daysArr[0] + ' days ' + daysArr[1] + ' hours';
      }
      // expiryTime =
      //   daysArr[0] !== '0' && daysArr[0] !== '-0'
      //     ? daysArr[0] + ' days ' + daysArr[1] + ' hours'
      //     : daysArr[1] + ' hours ' + daysArr[2] + 'min';
      return expiryTime;
    } else {
      return expiryTime;
    }
  }

  getRITMAttachmentDetails(sysId: string) {
    return this.apiService.get(ticketAPIConfig.getRitmAttachmentDetails, {}, [{ param: 'sysId', replaceWith: sysId }]);
  }

  getIncidentCountData(selectedValue: string, body: any) {
    const assignDetails: any = {};
    const incidentEndpoint = ticketAPIConfig.getIncidentCount;

    return this.apiService.post(incidentEndpoint, body, null, [{ param: 'requestPage', replaceWith: selectedValue }]);
  }

  addCommentsWorkNotes(payload: any, toggle: string) {
    let endPoint = ticketAPIConfig.updateTicket;

    if (toggle === 'task') {
      endPoint = ticketAPIConfig.updateServiceTask;
    }

    return this.apiService.post(endPoint, payload);
  }

  getSCTaskCountData(selectedValue: string, body: any) {
    const sctaskEndpoint = ticketAPIConfig.getServiceTaskCount;
    const assignDetails: any = {};

    return this.apiService.post(sctaskEndpoint, body, null, [{ param: 'requestPage', replaceWith: selectedValue }]);
  }

  getAutomationGraphDetailsIncident(body: any) {
    return this.apiService.post(ticketAPIConfig.getAutomationForIncident, body);
  }

  getAutomationGraphDetailsSCTask(body: any) {
    return this.apiService.post(ticketAPIConfig.getAutomationForSCTask, body);
  }

  getAssignmentGroups() {
    this.authService.getUserSysId().then((userSysID: any) => {
      this.loggedInUser = userSysID;
      this.getAssignmentGroupForUser(userSysID).subscribe(assignmentGroups => {
        this.assignmentGroups = assignmentGroups;
      });
    });
  }

  showActivityNote(body: any) {
    return this.apiService.post(ticketAPIConfig.showActivityNote, body);
  }

  fetchRCADetails(incident_sys_id: any, insights_type: any) {
    return this.apiService.get(ticketAPIConfig.fetchRCADetails, {}, [
      { param: 'incident_sys_id', replaceWith: incident_sys_id },
      { param: 'insights_type', replaceWith: insights_type }
    ]);
  }

  fetchRCADetailsEI(body: any) {
    return this.apiService.post(ticketAPIConfig.fetchRCADetailsEI, body);
  }

  saveinsightsRating(body: any) {
    return this.apiService.post(ticketAPIConfig.saveinsightsRating, body);
  }

  getDynamicMenuasPerTenant(configName: any) {
    //
    console.log('service called getDynamicMenuasPerTenantx ');
    return this.apiService.get(ticketAPIConfig.getDynamicMenuAsPerTenant, {}, [
      { param: 'configName', replaceWith: configName }
    ]);
  }

  sortOnFlag(a: any, b: any) {
    return a.checkresolveflag === b.checkresolveflag ? 0 : +(a.checkresolveflag > b.checkresolveflag) || -1;
  }

  getBotDataList() {
    return this.apiService.get(ticketAPIConfig.getSolutionList);
  }

  getSolutionDetails(body: any) {
    return this.apiService.post(ticketAPIConfig.getSolutionDetails, body);
  }

  getJBPMProcesses(body: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    return this.apiService.post(
      ticketAPIConfig.getJBPMProcesses,
      body,
      {
        headers,
        params: {
          mapper: 'NodeInstances'
        }
      },
      [{ param: 'queryName', replaceWith: 'jbpmProcessInstanceLogs' }]
    );
  }

  getJBPMProcessImage(processId: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    const requestOptions: any = {
      headers,
      responseType: 'text',
      params: {
        svgCompletedColor: '#E3E3E3',
        svgCompletedBorderColor: '#5EA4CA',
        svgActiveBorderColor: '#BEBEBE'
      }
    };
    return this.apiService.get(ticketAPIConfig.getJBPMProcessImage, requestOptions, [
      { param: 'containerId', replaceWith: 'CIAP_SapOps_1.0.0-SNAPSHOT' },
      { param: 'processInstanceId', replaceWith: processId }
    ]);
  }

  /*
   * At run time CIAP/ITSM status will be checked against canonical
   * checkValidationStatus('Resolved',<dynamicValuefromAPI>) <-- this will be fetched from Canonical API
   * checkValidationStatus('Resolved',<dynamicValuefromAPI>) <-- this will be actual value in ITSM
   * Returns true if match is found
   *
   */
  checkValidationStatus(validationName: string, valueToCheckAgainst: string) {
    let checkBy: 'CIAPStatus' | 'ITSMStatus';

    const validations = this.ticketingSettings.validations;
    const validationValue = validations.find(
      vN => vN.validationName.toLowerCase() === validationName.toLowerCase() && vN.forType === this.getCanType()
    );

    if (validationValue.disableForITSMStatus) {
    } else {
      checkBy = 'CIAPStatus';
    }

    if (!validationValue) {
      console.error('No validation with name ' + validationName + ' found in ticketing settings.');
    }

    let matchSuccessful;
    let listOfCanonicalStatuses;
    let disableForITSMStatus;
    if (checkBy === 'ITSMStatus') {
      disableForITSMStatus = validationValue.disableForITSMStatus;
      listOfCanonicalStatuses = this.getStatusList(this.ticketDataType);
    } else if (checkBy === 'CIAPStatus') {
      const disableForCIAPStatus = validationValue.disableForCIAPStatus;
      listOfCanonicalStatuses = this.getStatusList(this.ticketDataType);
      disableForITSMStatus = [];

      for (const st of disableForCIAPStatus) {
        disableForITSMStatus.push(this.getStatusConstantValue(st, this.ticketDataType).itsm_value);
      }
    }
    if (
      listOfCanonicalStatuses &&
      disableForITSMStatus &&
      listOfCanonicalStatuses.some(r => disableForITSMStatus.includes(r))
    ) {
      matchSuccessful =
        disableForITSMStatus.indexOf(this.getStatusConstantValue(valueToCheckAgainst, this.ticketDataType).itsm_value) >
        -1;
    }
    return matchSuccessful;
  }

  getWorkNotesComments(ticketSysId: string, selectedToggle) {
    let endPoint;
    if (selectedToggle.toLowerCase() === 'incident') {
      endPoint = ticketAPIConfig.getWorkNotesIncident;
    } else if (selectedToggle.toLowerCase() === 'servicerequests') {
      endPoint = ticketAPIConfig.getWorkNotesTask;
    }

    return this.apiService.get(endPoint, null, [{ param: 'ticketSysId', replaceWith: ticketSysId }]);
  }

  getStatusList(type?: any) {
    type = this.getCanType(type);

    let statusList = [];
    // const data = this.ticketingConstants.TICKET_STATUS;
    const ticketingConstants = this.getAllStatusConstants(type);
    for (const statusObject of ticketingConstants) {
      if (!statusObject.itsm_value && statusObject.ciap_code === '') {
        statusList.push(statusObject.ciap_value);
      } else if (statusObject.itsm_value) {
        statusList.push(statusObject.itsm_value);
      }
    }
    statusList.splice(2, 0, 'All Open');
    if (statusList && statusList.length > 0) {
      statusList = [...new Set(statusList)]; // Distinct statuses only (P002613-1681)
    }
    return statusList;
  }

  getAllStatusConstants(type?: CanonicalType) {
    if (!type) {
      return this.statusConstants;
    }
    return this.statusConstants.filter(i => {
      return i.type === type;
    });
  }

  getJBPMChildHeader(referenceId: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    const body: {} = {
      'order-by': null,
      'order-asc': false,
      'query-params': [
        {
          'cond-column': 'processinstanceid',
          'cond-operator': 'IN',
          'cond-values': [referenceId]
        }
      ],
      'result-column-mapping': null,
      'order-by-clause': null
    };

    return this.apiService.post(ticketAPIConfig.getChildHeaderData, body, { headers });
  }
  compareOnRefId(a: any, b: any) {
    // Use toUpperCase() to ignore character casing
    const ticketA = +a.referenceId;
    const ticketB = +b.referenceId;

    let comparison = 0;
    if (ticketA > ticketB) {
      comparison = 1;
    } else if (ticketA < ticketB) {
      comparison = -1;
    }
    return comparison;
  }
  getAttachmentDetails(sysId: string): Observable<any> {
    return this.apiService.post(ticketAPIConfig.getAttachmentDetails, { id: sysId });
  }
  getJBPMTooltip(nodeId: any, referenceId: any) {
    const headers = new HttpHeaders().set('JBPMCT-Authorization', 'Basic d2JhZG1pbjp3YmFkbWlu');
    const body: {} = {
      'order-by': null,
      'order-asc': false,
      'query-params': [
        {
          'cond-column': 'nodeId',
          'cond-operator': 'IN',
          'cond-values': [nodeId]
        },
        {
          'cond-column': 'processinstanceid',
          'cond-operator': 'IN',
          'cond-values': [referenceId]
        }
      ],
      'result-column-mapping': null,
      'order-by-clause': null
    };
    return this.apiService.post(ticketAPIConfig.getTooltipDataJBPM, body, { headers });
  }
  getIncidentScenarios(body: any) {
    return this.apiService.post(aiopsConfig.getIncidentScenarios, body);
  }
  showRelatedKOs(ticketId?: string) {
    return this.apiService.get(ticketAPIConfig.showRelatedKOs, { params: { ticketId } });
  }
  getTicketDetailData(ticketId: string, selectedToggle) {
    if (selectedToggle.toLowerCase() === 'incident') {
      return this.apiService.post(ticketAPIConfig.getIncidentWithAttachmentKODetails, {
        id: ticketId
      });
    }
    if (selectedToggle === 'ServiceRequests') {
      return this.apiService.post(ticketAPIConfig.getTaskWithAttachmentKODetails, {
        id: ticketId
      });
    }
    // console.log();
    // const userId = this.authService.getAuthenticatedUserId();
    // const globalSearchTxt = this.dataStore.getData(true, userId, 'ticketingGlobalSearch');
    // if (this.ticketDataType === 'automationApproval') {
    //   if (this.autoTaskSelected$) {
    //     return this.apiService.post(ticketAPIConfig.getTaskWithAttachmentKODetails, {
    //       id: ticketId
    //     });
    //   } else {
    //     return this.apiService.post(ticketAPIConfig.getIncidentWithAttachmentKODetails, {
    //       id: ticketId
    //     });
    //   }
    // } else {
    //   if (this.ticketDataType !== 'myTasks' && this.ticketDataType !== 'assignmentGroupTasks') {
    //     return this.apiService.post(ticketAPIConfig.getIncidentWithAttachmentKODetails, {
    //       id: ticketId
    //     });
    //   } else {
    //     return this.apiService.post(ticketAPIConfig.getTaskWithAttachmentKODetails, {
    //       id: ticketId
    //     });
    //   }
    // }
  }

  showResolutionSummary(reqPayload) {
    return this.apiService.post(ticketAPIConfig.getResolutionSummary, reqPayload);
  }

  showResolutionNote(sysid) {
    return this.apiService.get(ticketAPIConfig.showResolutionNote, {}, [{ param: 'sysid', replaceWith: sysid }]);
  }

  calculateExpiry(sla: string) {
    let time = '';
    if (sla) {
      const slaDate: any = new Date(sla);
      const currentDate: any = new Date();
      const millisec = slaDate - currentDate;
      time = this.getExpiryTime(millisec);
    }
    return time;
  }

  // Since ticketDataType does not store type as incident or task but rather as myTickets/assignmentGroupTickets or myTasks/assignmentGroupTasks, return the equivalent value
  getCanType(type?: any) {
    type = type || this.ticketDataType;

    if (!type) {
      return 'incident';
    }

    if (type && (type === 'myTickets' || type === 'assignmentGroupTickets')) {
      return 'incident';
    } else if ((type && type === 'myTasks') || type === 'assignmentGroupTasks') {
      return 'task';
      // commenting as status color was not displaying properly
      // } else if ((type && type === 'myTasks') || type === 'automationApproval') {
      //   return 'task';
    } else if (type === 'myApprovals') {
      if (this.autoTaskSelected$) {
        return 'task';
      } else {
        return 'incident';
      }
    }
    return type;
  }

  getStatusConstantValue(ciapValue: string, type: CanonicalType) {
    const matchedConstant = this.statusConstants.find(
      (c: any) =>
        (this.getCanType(ciapValue).toLowerCase() === this.getCanType(c.ciap_value).toLowerCase() ||
          this.getCanType(c.itsm_value).toLowerCase() === this.getCanType(ciapValue).toLowerCase()) &&
        (!c.type ||
          (this.getCanType(c.type) && this.getCanType(c.type).toLowerCase() === this.getCanType(type).toLowerCase()))
    );
    if (matchedConstant) {
      return matchedConstant;
    }
    console.log(
      'Cannot find static constant mapping for: ' +
        ciapValue +
        '. Please verify canonical data against internal mapping data.'
    );
    // console.error(
    //   'Cannot find static constant mapping for: ' +
    //     ciapValue +
    //     '. Please verify canonical data against internal mapping data.'
    // );
    return {
      itsm_value: '',
      itsm_code: ''
    };
  }

  // Method to get MyTickets Dashboard list data
  getDashboardData() {}

  //
  setDashboardData(ticketsDashboard: any[]) {
    this.dashboardData = ticketsDashboard;
    this.dashboardData.forEach(dashboardRow => {
      dashboardRow.isSelected = false;
    });
    if (ticketsDashboard.length === 0) {
      this.resetTicketData();
    }

    this._search$
      .pipe(
        tap(() => this._loading$.next(true)),
        switchMap(() => this._search()),
        tap(() => this._loading$.next(false))
      )
      .subscribe(
        result => {
          this._countries$.next(result.countries);
          this._total$.next(result.total);
          this._totalData$.next.bind(result.totalSortedData);
        },
        error => {
          // this._countries$.next([]);
          // this._total$.next(0);
          // this._totalData$.next([]);
        }
      );

    this._search$.next();
  }
  resetTicketData() {
    this.isTicketError$ = true;
    this.dashboardData = [];
    this._countries$.next([]);
    this._total$.next(0);
    this._totalData$.next([]);
  }

  private _search(): Observable<SearchResult> {
    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;
    this.dashboardData.forEach(dashboardRow => {
      if (
        dashboardRow.state_value &&
        dashboardRow.state_value.toLowerCase() ===
          this.getStatusConstantValue('Resolved', 'incident').itsm_value.toLowerCase()
      ) {
        dashboardRow.sla = 0;
      }
    });

    // 1. sort
    let countries = sort(this.dashboardData, sortColumn, sortDirection);
    const totalSortedData = sort(this.dashboardData, sortColumn, sortDirection);
    this.dashboardData = totalSortedData;
    // 2. filter
    countries = countries.filter(country => this.matches(country, searchTerm, this.pipe));
    const total = countries.length;

    // 3. paginate
    countries = pageSize ? countries.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize) : countries;
    return of({ countries, total, totalSortedData });
  }

  matches(country: any, term: string, pipe: PipeTransform) {
    country.incident_type = country.incident_type ? country.incident_type : '';
    country.number = country.number ? country.number : '';
    country.short_description = country.short_description ? country.short_description : '';
    country.priority_value = country.priority_value ? country.priority_value : '';
    country.application = country.application ? country.application : '';
    country.state_value = country.state_value ? country.state_value : '';
    country.solution_type = country.solution_type ? country.solution_type : '';
    country.technology = country.technology ? country.technology : '';
    country.assignment_group_value = country.assignment_group_value ? country.assignment_group_value : '';
    country.resolve_type = country.resolve_type ? country.resolve_type : '';
    country.ko_id = country.ko_id ? country.ko_id : '';
    country.solutionCategory = country.solutionCategory ? country.solutionCategory : '';
    country.solutionSubCategory = country.solutionSubCategory ? country.solutionSubCategory : '';
    country.calculatedExpiry = '';
    if (
      country.state_value !== this.getStatusConstantValue('Resolved', 'incident').itsm_value &&
      country.state_value !== this.getStatusConstantValue('closed', 'incident').itsm_value
    ) {
      country.calculatedExpiry = this.calculateExpiry(country.sla);
    } else {
      country.calculatedExpiry = '';
    }

    return (
      country.number.toLowerCase().includes(term) ||
      pipe.transform(country.incident_type.toLowerCase()).includes(term) ||
      pipe.transform(country.short_description.toLowerCase()).includes(term) ||
      pipe.transform(country.ko_id.toLowerCase()).includes(term) ||
      pipe.transform(country.priority_value.toLowerCase()).includes(term) ||
      pipe.transform(country.application.toLowerCase()).includes(term) ||
      pipe.transform(country.state_value.toLowerCase()).includes(term) ||
      pipe.transform(country.solution_type.toLowerCase()).includes(term) ||
      pipe.transform(country.technology.toLowerCase()).includes(term) ||
      pipe.transform(country.assignment_group_value.toLowerCase()).includes(term) ||
      pipe.transform(country.resolve_type.toLowerCase()).includes(term) ||
      pipe.transform(country.calculatedExpiry.toLowerCase()).includes(term) ||
      pipe.transform(country.solutionCategory.toLowerCase()).includes(term) ||
      pipe.transform(country.solutionSubCategory.toLowerCase()).includes(term)
    );
  }

  getNewTicketingStatusGraph(reqPayload, selectedToggle) {
    let endPoint;
    let type: string;
    if (selectedToggle === 'Incident') {
      type = 'incident';
      endPoint = ticketAPIConfig.getNewTicketingStatusGraph;
    }
    if (selectedToggle === 'ServiceRequests') {
      type = 'task';
      endPoint = ticketAPIConfig.getNewTicketingStatusGraphForSC;
    }
    // todo: Need to update this code once api configs are moved to root level
    //return this.apiService.post(endPoint, reqPayload);

    return this.apiService.post(endPoint, reqPayload, null, [
      { param: 'element', replaceWith: 'state' },
      { param: 'name', replaceWith: type }
    ]);
  }

  getNewTicketingPriorityGraph(reqPayload, selectedToggle) {
    // const endPoint = ticketAPIConfig.getNewTicketingPriorityGraph;
    // todo: Need to update this code once api configs are moved to root level
    //return this.apiService.post(endPoint, reqPayload);

    let endPoint;
    let type: string;
    if (selectedToggle === 'Incident') {
      type = 'incident';
      endPoint = ticketAPIConfig.getNewTicketingPriorityGraph;
    }
    if (selectedToggle === 'ServiceRequests') {
      type = 'task';
      endPoint = ticketAPIConfig.getNewTicketingPriorityGraphForSC;
    }
    // todo: Need to update this code once api configs are moved to root level
    //return this.apiService.post(endPoint, reqPayload);

    return this.apiService.post(endPoint, reqPayload, null, [
      { param: 'element', replaceWith: 'priority' },
      { param: 'name', replaceWith: type }
    ]);
  }

  getNewTicketingRecommendationGraph(reqPayload) {
    const endPoint = ticketAPIConfig.getNewTicketingRecommendationGraph;
    // todo: Need to update this code once api configs are moved to root level
    return this.apiService.post(endPoint, reqPayload);
  }

  getNewTicketingAssignmentGroupGraph(reqPayload, selectedToggle) {
    let endPoint;
    let type: string;
    if (selectedToggle === 'Incident') {
      endPoint = ticketAPIConfig.getTicketCountByAssignmentGroup;
    }
    if (selectedToggle === 'ServiceRequests') {
      endPoint = ticketAPIConfig.getTicketCountByAssignmentGroupForSC;
    }
    // todo: Need to update this code once api configs are moved to root level
    return this.apiService.post(endPoint, reqPayload);
  }

  getDurationRange(durationValue: any) {
    const toDate = this.dateTimeService.formatDateTimeToCIAPFormat(new Date(), null, true); // this.formatDate(new Date());
    const custDate = this.calculateLastDate(+durationValue - 1); // durationValue = either from preference or session
    const fromDate = this.dateTimeService.formatDateTimeToCIAPFormat(custDate, null, true); // this.formatDate(custDate);
    return {
      fromDate,
      toDate
    };
  }

  getIncidentKODetailsBasedOnNumber(ticketId: string, selectedToggle) {
    // if (this.ticketDataType === 'automationApproval') {
    //   if (this.autoTaskSelected$) {
    //     return this.apiService.get(ticketAPIConfig.getSCTaskDetailsBasedOnNumber, {}, [
    //       { param: 'number', replaceWith: ticketId }
    //     ]);
    //   } else {
    //     return this.apiService.get(ticketAPIConfig.getIncidentKODetailsBasedOnNumber, {}, [
    //       { param: 'number', replaceWith: ticketId }
    //     ]);
    //   }
    // } else if (this.ticketDataType !== 'myTasks' && this.ticketDataType !== 'assignmentGroupTasks') {
    //   return this.apiService.get(ticketAPIConfig.getIncidentKODetailsBasedOnNumber, {}, [
    //     { param: 'number', replaceWith: ticketId }
    //   ]);
    // } else {
    //   return this.apiService.get(ticketAPIConfig.getSCTaskDetailsBasedOnNumber, {}, [
    //     { param: 'number', replaceWith: ticketId }
    //   ]);
    // }

    if (selectedToggle === 'Incident') {
      return this.apiService.get(ticketAPIConfig.getIncidentKODetailsBasedOnNumber, {}, [
        { param: 'number', replaceWith: ticketId }
      ]);
    }
    if (selectedToggle === 'ServiceRequests') {
      return this.apiService.get(ticketAPIConfig.getSCTaskDetailsBasedOnNumber, {}, [
        { param: 'number', replaceWith: ticketId }
      ]);
    }
  }

  calculateLastDate(days: number) {
    // const calcDate = new Date();
    // if (days === 29) {
    //   calcDate.setMonth(calcDate.getMonth() - 1);
    // } else {
    //   calcDate.setDate(calcDate.getDate() - days);
    // }

    return moment()
      .subtract(days, 'days')
      .format('YYYY-MM-DD');
  }

  getAssignmentGroupTicketCountByElement(reqPayload, selectedToggle, groupToggle) {
    // todo: Need to update this code once api configs are moved to root level
    //return this.apiService.post(endPoint, reqPayload);

    let endPoint;
    let type: string;
    if (selectedToggle === 'Incident') {
      type = 'incident';
      endPoint = ticketAPIConfig.getAssignmentGroupTicketCountByElement;
    }
    if (selectedToggle === 'ServiceRequests') {
      type = 'task';
      endPoint = ticketAPIConfig.getAssignmentGroupTicketCountByElementForSC;
    }
    // todo: Need to update this code once api configs are moved to root level
    //return this.apiService.post(endPoint, reqPayload);

    return this.apiService.post(endPoint, reqPayload, null, [
      { param: 'element', replaceWith: groupToggle },
      { param: 'name', replaceWith: type }
    ]);
  }

  getTotalAndRecommendationTicketCountByAssigmentGroup(reqPayload, selectedToggle, groupToggle) {
    // todo: Need to update this code once api configs are moved to root level
    //return this.apiService.post(endPoint, reqPayload);

    let endPoint;
    let type: string;
    if (selectedToggle === 'Incident') {
      type = 'incident';
      endPoint = ticketAPIConfig.getTotalAndRecommendationTicketCountByAssigmentGroup;
    }
    if (selectedToggle === 'ServiceRequests') {
      type = 'task';
      endPoint = ticketAPIConfig.getTotalAndRecommendationTicketCountByAssigmentGroupSC;
    }
    // todo: Need to update this code once api configs are moved to root level
    //return this.apiService.post(endPoint, reqPayload);

    return this.apiService.post(endPoint, reqPayload, null, [
      { param: 'element', replaceWith: groupToggle },
      { param: 'name', replaceWith: type }
    ]);
  }

  updatesolutionworkflowstatus(ticketData: any) {
    if (this.autoTaskSelected$) {
      return this.apiService.post(ticketAPIConfig.updatesctasksolutionworkflowstatus, ticketData);
    } else {
      return this.apiService.post(ticketAPIConfig.updatesolutionworkflowstatus, ticketData);
    }
  }

  updatesolutionworkflowstatusForApproval(ticketData: any, selectedToggle) {
    if (selectedToggle === 'Incident') {
      return this.apiService.post(ticketAPIConfig.updatesolutionworkflowstatus, ticketData);
    }
    if (selectedToggle === 'ServiceRequests') {
      return this.apiService.post(ticketAPIConfig.updatesctasksolutionworkflowstatus, ticketData);
    }
  }

  getSolutionListOfAutomation() {
    return this.apiService.get(ticketAPIConfig.getSolutionListOfAutomation, {}, []);
  }

  checkUserOptionValue() {
    const userId = this.authService.getAuthenticatedUserId();
    let userData: any = {};
    userData = this.dataStore.getData(true, userId);
    return userData;
  }

  uploadPendingStateAttachment(attachmentDetails: any) {
    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;
    // todo: Need to update this code once api configs are moved to root level

    let endPoint = ticketAPIConfig.uploadAttachment;
    // if (this.checkUserOptionValue === '' || this.ticketDataType === 'assignmentGroupTasks') {
    //   endPoint = ticketAPIConfig.uploadAttachmentToServiceTask;
    // }

    return this.apiService.post(endPoint, attachmentDetails).pipe(
      tap((res: any) => {
        setTimeout(() => {
          this.toastService.showToast(
            [attachmentDetails.filename, 'app.lang.ticketing.toastMessage.pendingFileUploadSuccess'],
            'toastAcknowledgement',
            {
              classname: 'bg-success text-light'
            }
          );
        });
      })
    );
  }

  runBot(ticketData: any, selectedToggle) {
    const botParam = {
      sys_id: ticketData.sys_id,
      approval_status: 0,
      solutions: [],
      description: ticketData.description,
      created_by: 'CommandTower',
      queue_name: ticketData.queue_name,
      // subcategory: ticketData.subcategory,
      solution_execution_mode: 'CommandTower'
      // category: ticketData.category
    };
    // multi-solution change
    if (ticketData.solutions && ticketData.solutions.length > 0 && ticketData.solutions[0].solution_id) {
      // botParam.solutions = JSON.parse(JSON.stringify(ticketData.solutions));
      botParam.solutions = [
        {
          solution_id:
            ticketData.solutions[0].solution_id && ticketData.solutions[0].solution_id.length > 0
              ? ticketData.solutions[0].solution_id
              : null,
          allowresolve:
            ticketData.solutions[0].allowresolve && ticketData.solutions[0].allowresolve.length > 0
              ? ticketData.solutions[0].allowresolve
              : null,
          recommendation_message:
            ticketData.solutions[0].recommendation_message && ticketData.solutions[0].recommendation_message.length > 0
              ? ticketData.solutions[0].recommendation_message
              : null,
          checkresolveflag:
            ticketData.solutions[0].checkresolveflag && ticketData.solutions[0].checkresolveflag.length > 0
              ? ticketData.solutions[0].checkresolveflag
              : null,
          inputparams:
            ticketData.solutions[0].inputparams && ticketData.solutions[0].inputparams.length > 0
              ? ticketData.solutions[0].inputparams
              : null,
          solution_seq_no:
            ticketData.solutions[0].solution_seq_no && ticketData.solutions[0].solution_seq_no.length > 0
              ? ticketData.solutions[0].solution_seq_no
              : null,
          application_name:
            ticketData.solutions[0].application_name && ticketData.solutions[0].application_name.length > 0
              ? ticketData.solutions[0].application_name
              : null,
          application_url:
            ticketData.solutions[0].application_url && ticketData.solutions[0].application_url.length > 0
              ? ticketData.solutions[0].application_url
              : null,
          solution_score:
            ticketData.solutions[0].solution_score && ticketData.solutions[0].solution_score.length > 0
              ? ticketData.solutions[0].solution_score
              : null,
          proposed_timestamp:
            ticketData.solutions[0].proposed_timestamp && ticketData.solutions[0].proposed_timestamp.length > 0
              ? ticketData.solutions[0].proposed_timestamp
              : null,
          category:
            ticketData.solutions[0].category && ticketData.solutions[0].category.length > 0
              ? ticketData.solutions[0].category
              : null,
          subcategory:
            ticketData.solutions[0].subcategory && ticketData.solutions[0].subcategory.length > 0
              ? ticketData.solutions[0].subcategory
              : null,
          ner_required: ticketData.solutions[0].ner_required,
          ner_details: ticketData.solutions[0].ner_details
        }
      ];
    }

    const appConfig = this.runTimeConfigService.getConfig('appConfig');
    const envName = appConfig.environment;
    // todo: Need to update this code once api configs are moved to root level

    // let endPoint = ticketAPIConfig.rerouteTicket;
    // if (this.ticketDataType === 'myTasks' || this.ticketDataType === 'assignmentGroupTasks') {
    //   endPoint = ticketAPIConfig.rerouteServiceTask;
    // }

    let endPoint;
    if (selectedToggle === 'Incident') {
      endPoint = ticketAPIConfig.rerouteTicket;
    }
    if (selectedToggle === 'ServiceRequests') {
      endPoint = ticketAPIConfig.rerouteServiceTask;
    }

    return this.apiService.post(endPoint, botParam);
  }

  getIKONConfiguration() {
    return this.apiService.get(KnowledgeAPIConfig.getIKONConfiguration);
  }

  generateKOResolution(reqPayload, loadfromDbFlag) {
    return this.apiService.post(KnowledgeAPIConfig.generateKOResolution, reqPayload, null, [
      { param: 'loadFromDB', replaceWith: loadfromDbFlag }
    ]);
  }

  savetoKo(tck_id: any) {
    return this.apiService.post(KnowledgeAPIConfig.savetoKo, null, null, [{ param: 'ticket_id', replaceWith: tck_id }]);
  }
}
